import { useTheme } from '@mui/material';
import { useReactFlow, useStoreApi, Viewport } from '@xyflow/react';
import { useCallback, useEffect, useState } from 'react';
import { UseMeasureRect } from 'react-use/lib/useMeasure';

import { useViewportTransform } from '@/components/diagrams/FlowChart/hooks/useViewportTransform';

import { EntityDiagramEffectFn, FitViewEffect } from '../types';
import { getFitViewport } from '../utils/getFitViewport';

export function useFitViewEffect({
  containerDimensions,
  fitToCanvas,
}: {
  containerDimensions: UseMeasureRect;
  /**
   * Size the diagram against the React Flow canvas rather than the measured
   * container. Set where the canvas is scaled to fit a fixed-size slide, so
   * the container measurement doesn't describe the space the nodes actually
   * render into.
   */
  fitToCanvas: boolean;
}): EntityDiagramEffectFn<FitViewEffect> {
  const theme = useTheme();

  const { setViewport, getNodes, getNodesBounds } = useReactFlow();
  // Read the canvas dimensions imperatively at effect time rather than
  // subscribing — the provider that hosts this effect would otherwise re-render
  // on every React Flow store change.
  const store = useStoreApi();
  const { getTransform } = useViewportTransform();
  // Holds the initial viewport so we can navigate back to it later
  const [fitViewViewport, setFitViewViewport] = useState<Viewport | null>(null);
  useEffect(() => {
    // Reset the fit view viewport when the container dimensions change
    setFitViewViewport(null);
  }, [containerDimensions]);

  return useCallback<EntityDiagramEffectFn<FitViewEffect>>(
    (state, { perserveZoomLevel, kind, presentationMode }, _dispatch) => {
      const { isOnDispositionsFormDiagram } = state;
      const nodes = getNodes();
      const currentZoom = getTransform()[2];
      const minZoom = 0.5;
      const maxZoom = presentationMode ? 1.25 : 2;

      const zoomLevelOpts = {
        minZoom: perserveZoomLevel ? currentZoom : minZoom,
        maxZoom: perserveZoomLevel ? currentZoom : maxZoom,
      };

      // We care about all node types for anchoring and sizing in the Y
      // direction except for hidden ones
      const nodesToFitY = nodes.flatMap((node) => {
        if (node.hidden) {
          return [];
        }
        return node;
      });

      // For X centering and the width the zoom has to satisfy, include every
      // non-hidden node except section labels. Section labels are stretched to
      // the viewport width by `useSectionNodesResizeHandlers`, which inflates
      // the bounding box width and drags zoom below what the actual content
      // needs. Text-note and other content nodes must be included so they're
      // both kept in view by the zoom AND centered horizontally — otherwise a
      // user's graph note placed off to one side spills past the container edge
      // and clips (esp. in the narrower presentation slide).
      const nodesToFitForZoom = nodes.flatMap((node) => {
        if (node.hidden || node.type === 'sectionLabel') {
          return [];
        }
        return node;
      });

      // Get the bounds of the relevant nodes
      const nodesBoundsY = getNodesBounds(nodesToFitY);
      const nodesBoundsForZoom = getNodesBounds(nodesToFitForZoom);

      // Buffer applied around the content by fitView. Presentation and
      // DP-form containers are both sized to host the diagram tightly,
      // so a smaller buffer keeps the diagram from floating inside a
      // disproportionately large empty frame. The default stays looser
      // for the full-page entity-detail diagram where extra breathing
      // room is desirable.
      const paddingRatio =
        isOnDispositionsFormDiagram || presentationMode ? 0.08 : 0.15;

      // Applies a bit of space between the top of the container and the top of the nodes
      const topBuffer = presentationMode ? 40 : 80;

      const { width: canvasWidth, height: canvasHeight } = store.getState();

      const finalViewport =
        fitViewViewport ??
        getFitViewport({
          boundsForZoom: nodesBoundsForZoom,
          boundsY: nodesBoundsY,
          canvas: { width: canvasWidth, height: canvasHeight },
          container: containerDimensions,
          topBuffer,
          paddingRatio,
          fitToCanvas,
          ...zoomLevelOpts,
        });

      if (!fitViewViewport) {
        setFitViewViewport(finalViewport);
      }

      void setViewport(finalViewport, {
        duration:
          kind === 'init'
            ? undefined
            : theme.transitions.duration.enteringScreen,
      });
    },
    [
      containerDimensions,
      fitToCanvas,
      fitViewViewport,
      getNodes,
      getNodesBounds,
      getTransform,
      setViewport,
      store,
      theme.transitions.duration.enteringScreen,
    ]
  );
}
